/**
 * Walk-Forward Testing Engine
 * Version: 6.2.0
 * 
 * Implements rolling and anchored walk-forward analysis for strategy validation
 */

// Data point interface
export interface OHLCV {
  timestamp: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

// Walk-forward window configuration
export interface WFWindowConfig {
  trainSize: number  // Number of periods for in-sample training
  testSize: number    // Number of periods for out-of-sample testing
  stepSize: number    // Rolling window step (1 = continuous, >1 = gap)
  mode: 'rolling' | 'anchored'  // Rolling moves forward, anchored starts fresh
}

// Single walk-forward window result
export interface WFWindowResult {
  windowIndex: number
  startDate: number
  endDate: number
  trainStart: number
  trainEnd: number
  testStart: number
  testEnd: number
  metrics: WFMetrics
  equityCurve: number[]
  trades: WFTrade[]
}

// Walk-forward metrics
export interface WFMetrics {
  // In-sample (training) metrics
  trainReturn: number
  trainSharpe: number
  trainWinRate: number
  trainMaxDrawdown: number
  
  // Out-of-sample (testing) metrics
  testReturn: number
  testSharpe: number
  testWinRate: number
  testMaxDrawdown: number
  
  // Walk-forward efficiency ratio
  wfRatio: number  // test sharpe / train sharpe (closer to 1 = better)
  
  // Consistency metrics
  returnConsistency: number  // How consistent returns are across windows
  sharpeConsistency: number // How consistent sharpe across windows
}

// Trade during walk-forward
export interface WFTrade {
  entryDate: number
  exitDate: number
  entryPrice: number
  exitPrice: number
  type: 'LONG' | 'SHORT'
  pnl: number
  returnPct: number
}

// Walk-forward analysis result
export interface WFAnalysisResult {
  config: WFWindowConfig
  windows: WFWindowResult[]
  aggregateMetrics: WFAggregateMetrics
  bestWindow: number
  worstWindow: number
  overallEquity: number[]
}

// Aggregate metrics across all windows
export interface WFAggregateMetrics {
  avgReturn: number
  avgSharpe: number
  avgWinRate: number
  avgDrawdown: number
  totalTrades: number
  winRate: number
  maxDrawdown: number
  sharpeStdDev: number
  returnStdDev: number
}

// WalkForwardEngine class
export class WalkForwardEngine {
  private config: WFWindowConfig
  private data: OHLCV[]

  constructor(config: WFWindowConfig) {
    this.config = config
    this.data = []
  }

  // Load historical data
  loadData(data: OHLCV[]): void {
    this.data = data.sort((a, b) => a.timestamp - b.timestamp)
  }

  // Calculate walk-forward windows
  getWindows(): { train: [number, number], test: [number, number] }[] {
    const windows: { train: [number, number], test: [number, number] }[] = []
    const { trainSize, testSize, stepSize, mode } = this.config
    const totalSize = trainSize + testSize

    let startIdx = 0
    
    while (startIdx + totalSize <= this.data.length) {
      if (mode === 'rolling') {
        // Rolling: move train window forward
        windows.push({
          train: [startIdx, startIdx + trainSize],
          test: [startIdx + trainSize, startIdx + totalSize]
        })
      } else {
        // Anchored: always start from beginning
        windows.push({
          train: [0, trainSize],
          test: [startIdx + trainSize, startIdx + totalSize]
        })
      }
      
      startIdx += stepSize
    }

    return windows
  }

  // Run complete walk-forward analysis
  analyze(strategyFn: (data: OHLCV[]) => WFTrade[]): WFAnalysisResult {
    const windows = this.getWindows()
    const results: WFWindowResult[] = []

    windows.forEach((window, index) => {
      const trainData = this.data.slice(window.train[0], window.train[1])
      const testData = this.data.slice(window.test[0], window.test[1])

      // Run strategy on training data to optimize parameters
      const trainTrades = strategyFn(trainData)
      
      // Run same strategy on test data (out-of-sample)
      const testTrades = strategyFn(testData)

      // Calculate metrics
      const trainMetrics = this.calculateMetrics(trainTrades, trainData)
      const testMetrics = this.calculateMetrics(testTrades, testData)

      // Calculate equity curves
      const testEquity = this.calculateEquity(testTrades, testData)

      results.push({
        windowIndex: index,
        startDate: this.data[window.train[0]].timestamp,
        endDate: this.data[window.test[1] - 1].timestamp,
        trainStart: this.data[window.train[0]].timestamp,
        trainEnd: this.data[window.train[1] - 1].timestamp,
        testStart: this.data[window.test[0]].timestamp,
        testEnd: this.data[window.test[1] - 1].timestamp,
        metrics: {
          trainReturn: trainMetrics.return,
          trainSharpe: trainMetrics.sharpe,
          trainWinRate: trainMetrics.winRate,
          trainMaxDrawdown: trainMetrics.maxDrawdown,
          testReturn: testMetrics.return,
          testSharpe: testMetrics.sharpe,
          testWinRate: testMetrics.winRate,
          testMaxDrawdown: testMetrics.maxDrawdown,
          wfRatio: testMetrics.sharpe / (trainMetrics.sharpe || 1),
          returnConsistency: this.calculateConsistency(results.map(r => r.metrics.testReturn)),
          sharpeConsistency: this.calculateConsistency(results.map(r => r.metrics.testSharpe))
        },
        equityCurve: testEquity,
        trades: testTrades
      })
    })

    // Calculate aggregate metrics
    const aggregate = this.calculateAggregate(results)

    // Find best and worst windows
    const sharpes = results.map(r => r.metrics.testSharpe)
    const bestIdx = sharpes.indexOf(Math.max(...sharpes))
    const worstIdx = sharpes.indexOf(Math.min(...sharpes))

    return {
      config: this.config,
      windows: results,
      aggregateMetrics: aggregate,
      bestWindow: bestIdx,
      worstWindow: worstIdx,
      overallEquity: this.calculateOverallEquity(results)
    }
  }

  // Calculate metrics for a set of trades
  private calculateMetrics(trades: WFTrade[], data: OHLCV[]): {
    return: number
    sharpe: number
    winRate: number
    maxDrawdown: number
  } {
    if (trades.length === 0) {
      return { return: 0, sharpe: 0, winRate: 0, maxDrawdown: 0 }
    }

    const returns = trades.map(t => t.returnPct)
    
    // Total return
    const totalReturn = returns.reduce((sum, r) => sum + r, 0)

    // Win rate
    const wins = returns.filter(r => r > 0).length
    const winRate = wins / returns.length

    // Sharpe ratio (simplified)
    const avgReturn = totalReturn / returns.length
    const returnStd = Math.sqrt(
      returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) / returns.length
    )
    const sharpe = returnStd > 0 ? (avgReturn / returnStd) * Math.sqrt(252) : 0

    // Max drawdown
    const equity = this.calculateEquity(trades, data)
    const maxDrawdown = this.calculateMaxDrawdown(equity)

    return { return: totalReturn, sharpe, winRate, maxDrawdown }
  }

  // Calculate equity curve
  private calculateEquity(trades: WFTrade[], data: OHLCV[]): number[] {
    const equity: number[] = []
    let cumulative = 0

    // Create equity timeline
    for (let i = 0; i < data.length; i++) {
      // Add P&L for trades that closed at this timestamp
      const closedTrades = trades.filter(t => t.exitDate === data[i].timestamp)
      for (const trade of closedTrades) {
        cumulative += trade.pnl
      }
      equity.push(cumulative)
    }

    return equity
  }

  // Calculate max drawdown
  private calculateMaxDrawdown(equity: number[]): number {
    let maxDrawdown = 0
    let peak = -Infinity

    for (const value of equity) {
      if (value > peak) peak = value
      const drawdown = (peak - value) / (peak || 1)
      if (drawdown > maxDrawdown) maxDrawdown = drawdown
    }

    return maxDrawdown
  }

  // Calculate consistency (coefficient of variation inverse)
  private calculateConsistency(values: number[]): number {
    if (values.length < 2) return 1
    
    const mean = values.reduce((a, b) => a + b, 0) / values.length
    const std = Math.sqrt(
      values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length
    )
    
    if (mean === 0) return 0
    return 1 - (std / Math.abs(mean))
  }

  // Calculate aggregate metrics
  private calculateAggregate(results: WFWindowResult[]): WFAggregateMetrics {
    const returns = results.map(r => r.metrics.testReturn)
    const sharpes = results.map(r => r.metrics.testSharpe)
    const winRates = results.map(r => r.metrics.testWinRate)
    const drawdowns = results.map(r => r.metrics.testMaxDrawdown)
    const allTrades = results.flatMap(r => r.trades)

    const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length
    const std = (arr: number[]) => {
      const mean = avg(arr)
      return Math.sqrt(arr.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / arr.length)
    }

    const wins = allTrades.filter(t => t.pnl > 0).length

    return {
      avgReturn: avg(returns),
      avgSharpe: avg(sharpes),
      avgWinRate: avg(winRates),
      avgDrawdown: avg(drawdowns),
      totalTrades: allTrades.length,
      winRate: allTrades.length > 0 ? wins / allTrades.length : 0,
      maxDrawdown: Math.max(...drawdowns),
      sharpeStdDev: std(sharpes),
      returnStdDev: std(returns)
    }
  }

  // Calculate overall equity across all windows
  private calculateOverallEquity(results: WFWindowResult[]): number[] {
    const allEquity: number[] = []
    for (const result of results) {
      allEquity.push(...result.equityCurve)
    }
    return allEquity
  }
}

// Quick analysis function
export function quickWalkForwardAnalysis(
  data: OHLCV[],
  trainSize: number,
  testSize: number,
  stepSize: number = 1,
  mode: 'rolling' | 'anchored' = 'rolling'
): WFAnalysisResult {
  const engine = new WalkForwardEngine({ trainSize, testSize, stepSize, mode })
  engine.loadData(data)
  
  // Default strategy (RSI-based)
  const defaultStrategy = (d: OHLCV[]) => {
    const trades: WFTrade[] = []
    let position = 0
    
    for (let i = 1; i < d.length; i++) {
      const rsi = calculateRSI(d.slice(0, i + 1))
      
      if (rsi < 30 && position === 0) {
        position = 1
      } else if (rsi > 70 && position === 1) {
        trades.push({
          entryDate: d[i - 1].timestamp,
          exitDate: d[i].timestamp,
          entryPrice: d[i - 1].close,
          exitPrice: d[i].close,
          type: 'LONG',
          pnl: d[i].close - d[i - 1].close,
          returnPct: ((d[i].close - d[i - 1].close) / d[i - 1].close) * 100
        })
        position = 0
      }
    }
    
    return trades
  }
  
  return engine.analyze(defaultStrategy)
}

// RSI calculation helper
function calculateRSI(data: OHLCV[], period: number = 14): number {
  if (data.length < period + 1) return 50
  
  let gains = 0
  let losses = 0
  
  for (let i = data.length - period; i < data.length; i++) {
    const change = data[i].close - data[i - 1].close
    if (change > 0) gains += change
    else losses -= change
  }
  
  const avgGain = gains / period
  const avgLoss = losses / period
  
  if (avgLoss === 0) return 100
  const rs = avgGain / avgLoss
  return 100 - (100 / (1 + rs))
}

export default WalkForwardEngine